import type { GetAdminV1OrgMediaParams } from '@nucleus-commerce/api-client/admin';

export const MEDIA_DATE_PRESETS = ['any', 'today', 'last7', 'last30', 'last90', 'custom'] as const;
export type MediaDatePreset = (typeof MEDIA_DATE_PRESETS)[number];

/** The upload-date filter as the user set it; `from` and `to` only matter for `custom`. */
export interface MediaDateFilterValue {
  preset: MediaDatePreset;
  from?: string;
  to?: string;
}

export const ANY_DATE: MediaDateFilterValue = { preset: 'any' };

export const isMediaDatePreset = (value: string): value is MediaDatePreset =>
  (MEDIA_DATE_PRESETS as readonly string[]).includes(value);

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;
const PRESET_DAYS: Partial<Record<MediaDatePreset, number>> = {
  today: 1,
  last7: 7,
  last30: 30,
  last90: 90,
};

/** Today as the backend counts it: media belongs to the organization, which filters on UTC days. */
export function utcToday(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

function daysBefore(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) - days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The list params for an upload-date filter. Presets count today as one of their days; a custom
 * range ignores a half-typed day and swaps its ends when they are given backwards.
 */
export function mediaDateParams(
  value: MediaDateFilterValue,
  now = new Date(),
): Pick<GetAdminV1OrgMediaParams, 'createdAt_gte' | 'createdAt_lte'> {
  const days = PRESET_DAYS[value.preset];
  if (days !== undefined) return { createdAt_gte: daysBefore(utcToday(now), days - 1) };
  if (value.preset !== 'custom') return {};
  const from = value.from && DAY.test(value.from) ? value.from : undefined;
  const to = value.to && DAY.test(value.to) ? value.to : undefined;
  const [gte, lte] = from && to && from > to ? [to, from] : [from, to];
  return { ...(gte ? { createdAt_gte: gte } : {}), ...(lte ? { createdAt_lte: lte } : {}) };
}

export function isDateFiltered(value: MediaDateFilterValue): boolean {
  return Object.keys(mediaDateParams(value)).length > 0;
}
